import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { UserProgress } from '../../types/workout';
import { WorkoutUtils } from '../../utils/workoutUtils';

// Import exercises data to get RPE information
import exercisesData from '../../exercises.json';

interface PersonalRecordsChartsProps {
  userProgress: UserProgress | null;
}

type ChartType = 'pr-timeline' | 'top-records' | 'pr-frequency' | 'rpe-analysis';

const CHART_COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1',
  '#d084d0', '#ffb347', '#87ceeb', '#dda0dd', '#98fb98'
];

export const PersonalRecordsCharts: React.FC<PersonalRecordsChartsProps> = ({ userProgress }) => {
  const [selectedChart, setSelectedChart] = useState<ChartType>('pr-timeline');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleChartChange = (event: SelectChangeEvent) => {
    setSelectedChart(event.target.value as ChartType);
  };

  const chartOptions = [
    { value: 'pr-timeline', label: 'PR Timeline', description: 'Personal records achieved over time' },
    { value: 'top-records', label: 'Top Records', description: 'Highest weights and volumes achieved' },
    { value: 'pr-frequency', label: 'PR Frequency', description: 'How often you set new records' },
    { value: 'rpe-analysis', label: 'RPE Analysis', description: 'Effort levels when setting personal records' },
  ];

  const selectedOption = chartOptions.find(option => option.value === selectedChart);

  // Get RPE data for personal records with proper exercise definitions
  const getPRRPEData = () => {
    if (!userProgress) return [];

    const prTimeline = WorkoutUtils.getPersonalRecordsTimeline(userProgress);
    const prRPEData: any[] = [];

    prTimeline.forEach(pr => {
      // Find the exercise definition across all weeks and days
      let exerciseDefinition: any = null;
      
      Object.values(exercisesData).forEach((weekData: any) => {
        Object.values(weekData).forEach((dayExercises: any) => {
          if (Array.isArray(dayExercises)) {
            const found = dayExercises.find((ex: any) => ex.exercise === pr.exerciseName);
            if (found) {
              exerciseDefinition = found;
            }
          }
        });
      });

      if (!exerciseDefinition) return;

      // Find the workout session where this PR was achieved
      const prSession = userProgress.workoutHistory.find(session => 
        session.week === pr.week && session.completed
      );

      if (!prSession) return;

      // Find the exercise in that session
      const prExercise = prSession.exercises.find(ex => ex.exerciseName === pr.exerciseName);
      if (!prExercise) return;

      // Get working sets with actual RPE
      const workingSets = prExercise.sets.filter(set => 
        set.setType === 'working' && 
        set.completed && 
        set.actualRpe
      );

      if (workingSets.length === 0) return;

      // Calculate average RPE for this PR session
      let totalActualRPE = 0;
      let totalExpectedRPE = 0;
      let validSets = 0;

      workingSets.forEach((set, index) => {
        const isLastSet = index === workingSets.length - 1;
        const expectedRPEString = isLastSet 
          ? exerciseDefinition.last_set_rpe 
          : exerciseDefinition.early_set_rpe;

        if (expectedRPEString !== "N/A") {
          const expectedRPE = WorkoutUtils.rpeToNumber(expectedRPEString);
          const actualRPE = WorkoutUtils.rpeToNumber(set.actualRpe!);
          
          if (expectedRPE > 0 && actualRPE > 0) {
            totalExpectedRPE += expectedRPE;
            totalActualRPE += actualRPE;
            validSets++;
          }
        }
      });

      if (validSets > 0) {
        const avgExpectedRPE = totalExpectedRPE / validSets;
        const avgActualRPE = totalActualRPE / validSets;

        prRPEData.push({
          exerciseName: pr.exerciseName,
          week: pr.week,
          recordType: pr.recordType,
          value: pr.value,
          expectedRPE: Math.round(avgExpectedRPE * 10) / 10,
          actualRPE: Math.round(avgActualRPE * 10) / 10,
          rpeEfficiency: Math.abs(avgActualRPE - avgExpectedRPE)
        });
      }
    });

    return prRPEData;
  };

  const renderChart = () => {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No personal records data available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete more workouts to set personal records and see charts
          </Typography>
        </Box>
      );
    }

    switch (selectedChart) {
      case 'pr-timeline':
        const timelineData = WorkoutUtils.getPersonalRecordsTimeline(userProgress);
        
        if (timelineData.length === 0) {
          return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No personal records yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Keep training to set your first personal records!
              </Typography>
            </Box>
          );
        }

        // Group by week for timeline visualization
        const weeklyPRs = timelineData.reduce((acc, pr) => {
          if (!acc[pr.week]) {
            acc[pr.week] = { week: pr.week, weightPRs: 0, volumePRs: 0, totalPRs: 0 };
          }
          if (pr.recordType === 'weight') acc[pr.week].weightPRs++;
          if (pr.recordType === 'volume') acc[pr.week].volumePRs++;
          acc[pr.week].totalPRs++;
          return acc;
        }, {} as any);

        const timelineChartData = Object.values(weeklyPRs);

        return (
          <Box sx={{ 
            width: '100%', 
            height: isMobile ? 300 : 400,
            '& .recharts-wrapper': {
              pointerEvents: 'none !important'
            }
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={timelineChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="week" 
                  label={{ value: 'Week', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  label={{ value: 'Personal Records', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    value,
                    name === 'weightPRs' ? 'Weight PRs' : 
                    name === 'volumePRs' ? 'Volume PRs' : 'Total PRs'
                  ]}
                  labelFormatter={(week) => `Week ${week}`}
                />
                <Bar 
                  dataKey="weightPRs" 
                  stackId="prs"
                  fill="#8884d8"
                  name="weightPRs"
                />
                <Bar 
                  dataKey="volumePRs" 
                  stackId="prs"
                  fill="#82ca9d"
                  name="volumePRs"
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        );

      case 'top-records':
        const topRecords = WorkoutUtils.getTopPersonalRecords(userProgress);
        
        return (
          <Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              {/* Top Weight Records */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant={isMobile ? "subtitle2" : "h6"} gutterBottom>
                  Top Weight Records
                </Typography>
                <TableContainer 
                  component={Paper} 
                  variant="outlined" 
                  sx={{ 
                    maxHeight: 400,
                    '& .MuiTable-root': {
                      minWidth: 'auto'
                    }
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Exercise</strong></TableCell>
                        <TableCell align="right"><strong>Weight</strong></TableCell>
                        <TableCell align="right"><strong>Week</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topRecords.weightRecords.length > 0 ? (
                        topRecords.weightRecords.slice(0, 8).map((record, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ 
                              maxWidth: isMobile ? 100 : 120, 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: isMobile ? '0.75rem' : '0.875rem'
                            }}>
                              {record.exerciseName}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                              {record.weight} lbs
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                              {record.week}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No weight records yet
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Top Volume Records */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant={isMobile ? "subtitle2" : "h6"} gutterBottom>
                  Top Volume Records
                </Typography>
                <TableContainer 
                  component={Paper} 
                  variant="outlined" 
                  sx={{ 
                    maxHeight: 400,
                    '& .MuiTable-root': {
                      minWidth: 'auto'
                    }
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Exercise</strong></TableCell>
                        <TableCell align="right"><strong>Volume</strong></TableCell>
                        <TableCell align="right"><strong>Week</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topRecords.volumeRecords.length > 0 ? (
                        topRecords.volumeRecords.slice(0, 8).map((record, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ 
                              maxWidth: isMobile ? 100 : 120, 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: isMobile ? '0.75rem' : '0.875rem'
                            }}>
                              {record.exerciseName}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                              {record.volume.toLocaleString()} lbs
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                              {record.week}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No volume records yet
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Stack>
          </Box>
        );

      case 'pr-frequency':
        const prFrequencyData = WorkoutUtils.getPersonalRecordsTimeline(userProgress);
        
        if (prFrequencyData.length === 0) {
          return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No personal records yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Keep training to set your first personal records!
              </Typography>
            </Box>
          );
        }

        // Calculate PRs per exercise
        const exercisePRCounts = prFrequencyData.reduce((acc, pr) => {
          if (!acc[pr.exerciseName]) {
            acc[pr.exerciseName] = { exerciseName: pr.exerciseName, weightPRs: 0, volumePRs: 0, totalPRs: 0 };
          }
          if (pr.recordType === 'weight') acc[pr.exerciseName].weightPRs++;
          if (pr.recordType === 'volume') acc[pr.exerciseName].volumePRs++;
          acc[pr.exerciseName].totalPRs++;
          return acc;
        }, {} as any);

        const frequencyChartData = Object.values(exercisePRCounts)
          .sort((a: any, b: any) => b.totalPRs - a.totalPRs)
          .slice(0, 10);

        if (frequencyChartData.length === 0) {
          return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No PR frequency data available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Complete more workouts to build PR frequency data
              </Typography>
            </Box>
          );
        }

        return (
          <Box sx={{ 
            width: '100%', 
            height: isMobile ? 350 : 400,
            '& .recharts-wrapper': {
              pointerEvents: 'none !important'
            }
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={frequencyChartData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="number"
                  label={{ value: 'Number of PRs', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  type="category"
                  dataKey="exerciseName"
                  width={isMobile ? 120 : 150}
                  tick={{ fontSize: isMobile ? 9 : 12 }}
                  tickFormatter={(value) => isMobile && value.length > 15 ? `${value.substring(0, 15)}...` : value}
                />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    value,
                    name === 'weightPRs' ? 'Weight PRs' : 
                    name === 'volumePRs' ? 'Volume PRs' : 'Total PRs'
                  ]}
                />
                <Bar 
                  dataKey="weightPRs" 
                  stackId="prs"
                  fill="#8884d8"
                  name="weightPRs"
                />
                <Bar 
                  dataKey="volumePRs" 
                  stackId="prs"
                  fill="#82ca9d"
                  name="volumePRs"
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        );

      case 'rpe-analysis':
        const prRPEData = getPRRPEData();
        
        console.log('🔍 PR RPE Data:', prRPEData.length, prRPEData.slice(0, 3));

        if (prRPEData.length === 0) {
          return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No RPE data for personal records
              </Typography>
              <Typography variant="body2" color="text.secondary">
                RPE analysis requires personal records with corresponding RPE tracking data.
              </Typography>
            </Box>
          );
        }

        // Group by RPE level to show distribution
        const rpeDistribution = prRPEData.reduce((acc, data) => {
          const rpeLevel = Math.round(data.actualRPE);
          const rpeKey = `RPE ${rpeLevel}`;
          
          if (!acc[rpeKey]) {
            acc[rpeKey] = { 
              rpeLevel: rpeKey, 
              count: 0, 
              weightPRs: 0, 
              volumePRs: 0,
              exercises: new Set()
            };
          }
          
          acc[rpeKey].count += 1;
          acc[rpeKey].exercises.add(data.exerciseName);
          
          if (data.recordType === 'weight') {
            acc[rpeKey].weightPRs += 1;
          } else {
            acc[rpeKey].volumePRs += 1;
          }
          
          return acc;
        }, {} as any);

        const rpeChartData = Object.values(rpeDistribution)
          .map((item: any) => ({
            ...item,
            exerciseCount: item.exercises.size
          }))
          .sort((a: any, b: any) => {
            const aNum = parseInt(a.rpeLevel.replace('RPE ', ''));
            const bNum = parseInt(b.rpeLevel.replace('RPE ', ''));
            return aNum - bNum;
          });

        return (
          <Box sx={{ 
            width: '100%', 
            height: isMobile ? 300 : 400,
            '& .recharts-wrapper': {
              pointerEvents: 'none !important'
            }
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rpeChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="rpeLevel" 
                  label={{ value: 'Effort Level', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  label={{ value: 'Number of PRs', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    value,
                    name === 'weightPRs' ? 'Weight PRs' : 
                    name === 'volumePRs' ? 'Volume PRs' : 'Total PRs'
                  ]}
                  labelFormatter={(rpeLevel, payload) => {
                    if (payload && payload[0]) {
                      const data = payload[0].payload;
                      return `${rpeLevel} (${data.exerciseCount} exercises)`;
                    }
                    return rpeLevel;
                  }}
                />
                <Bar 
                  dataKey="weightPRs" 
                  stackId="prs"
                  fill="#8884d8"
                  name="weightPRs"
                />
                <Bar 
                  dataKey="volumePRs" 
                  stackId="prs"
                  fill="#82ca9d"
                  name="volumePRs"
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'flex-start' }, 
          mb: 3,
          gap: { xs: 2, sm: 0 }
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
              Personal Records Analysis
            </Typography>
            {selectedOption && (
              <Typography variant="body2" color="text.secondary">
                {selectedOption.description}
              </Typography>
            )}
          </Box>
          
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
            <Select
              value={selectedChart}
              onChange={handleChartChange}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    zIndex: 1400,
                  },
                },
              }}
            >
              {chartOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {option.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {renderChart()}
      </CardContent>
    </Card>
  );
}; 